import React, { useRef, useState, useEffect } from "react";
import { FaCheck } from "react-icons/fa";
import { FolderProgress } from "../hooks/folderProgress";

interface TiltedCardProps {
    imageSrc: React.ComponentProps<"img">["src"];
    altText?: string;
    name: string;
    order: number;
    onClick?: () => void;
    captionText?: string;
    containerHeight?: React.CSSProperties['height'];
    containerWidth?: React.CSSProperties['width'];
    imageHeight?: React.CSSProperties['height'];
    imageWidth?: React.CSSProperties['width'];
    scaleOnHover?: number;
    rotateAmplitude?: number;
    showMobileWarning?: boolean;
    showTooltip?: boolean;
    overlayContent?: React.ReactNode;
    displayOverlayContent?: boolean;
    // Completed lectures inside this folder, for the signed-in user
    progress?: FolderProgress | null;
}

export default function TiltedCard({
    imageSrc,
    altText = "Tilted card image",
    name,
    order,
    onClick,
    captionText = "",
    containerHeight = "320px",
    containerWidth = "240px",
    imageHeight = "100%",
    imageWidth = "100%",
    scaleOnHover = 1.1,
    rotateAmplitude = 30,
    showMobileWarning = false,
    showTooltip = true,
    overlayContent = null,
    displayOverlayContent = false,
    progress = null,
}: TiltedCardProps) {
    // All hooks must be called before any conditional returns
    const cardRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [mouseLeaveDelay, setMouseLeaveDelay] = useState<NodeJS.Timeout | null>(null);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        if (cardRef.current) {
            setDimensions({
                width: cardRef.current.offsetWidth,
                height: cardRef.current.offsetHeight
            });
        }

        // Check if mobile
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 640);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    if (!imageSrc) {
        console.error('TiltedCard: imageSrc is required');
        return <div>Error: Image source is required</div>;
    }

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!cardRef.current) return;

        const rect = cardRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left - dimensions.width / 2;
        const y = e.clientY - rect.top - dimensions.height / 2;

        setMousePosition({ x, y });
    };

    const handleMouseEnter = () => {
        if (mouseLeaveDelay) {
            clearTimeout(mouseLeaveDelay);
            setMouseLeaveDelay(null);
        }
    };

    const handleMouseLeave = () => {
        const timeout = setTimeout(() => {
            setMousePosition({ x: 0, y: 0 });
        }, 1000);
        setMouseLeaveDelay(timeout);
    };

    // Calculate mouse position percentages
    const mousePX = dimensions.width > 0 ? mousePosition.x / dimensions.width : 0;
    const mousePY = dimensions.height > 0 ? mousePosition.y / dimensions.height : 0;

    // Calculate rotation values
    const rX = mousePX * rotateAmplitude;
    const rY = mousePY * -rotateAmplitude;

    // Calculate translation values for background
    const tX = mousePX * -40;
    const tY = mousePY * -40;

    const cardStyle = {
        transform: `rotateY(${rX}deg) rotateX(${rY}deg)`,
        width: '100%',
        height: '100%',
    };

    const cardBgStyle = {
        transform: `translateX(${tX}px) translateY(${tY}px)`,
        backgroundImage: `url(${imageSrc})`,
    };

    return (
        <div
            className='group relative block sm:perspective-800 sm:transform-style-3d'
            ref={cardRef}
            onMouseMove={handleMouseMove}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={onClick}
            style={{
                cursor: onClick ? 'pointer' : 'default',
                width: containerWidth,
                height: containerHeight,
                transform: !isMobile ? 'perspective(800px)' : 'none',
                transformStyle: !isMobile ? 'preserve-3d' : 'flat'
            }}
        >
            <div
                className='relative w-full h-full bg-[#333] overflow-hidden rounded-[10px] shadow-card-default transition-card-default group-hover:shadow-card-hover group-hover:transition-card-hover sm:shadow-card-default max-sm:shadow-card-mobile'
                style={cardStyle}
            >
                <div
                    className='absolute -top-5 -left-5 w-card-bg h-card-bg bg-no-repeat bg-center bg-cover opacity-[0.85] pointer-events-none transition-card-bg group-hover:opacity-100 group-hover:transition-card-bg-hover'
                    style={cardBgStyle}
                ></div>

                {progress && (
                    progress.completed >= progress.total ? (
                        <div className='absolute top-2.5 right-2.5 z-10 flex items-center gap-1.5 rounded-lg bg-green-600 px-2.5 py-1 text-sm font-bold text-white shadow-small'>
                            <FaCheck aria-hidden='true' className='text-xs' />
                            Completed
                        </div>
                    ) : (
                        <div className='absolute top-2.5 right-2.5 z-10 rounded-lg bg-black/60 px-2.5 py-1 text-sm font-bold text-white font-inter-numbers'>
                            <span aria-hidden='true'>{progress.completed} / {progress.total}</span>
                            <span className='sr-only'>{progress.completed} of {progress.total} lectures completed</span>
                        </div>
                    )
                )}

                {/* Footer that mimics original CardFooter styling */}
                <div className='justify-between before:bg-lightBlue/10 border-lightGrey/90 border-1 overflow-hidden py-2 absolute before:rounded-xl rounded-large bottom-1 w-[calc(100%_-_8px)] shadow-small ml-1 z-10 bg-black/60 dark:bg-black/60 flex items-center px-3 min-h-[60px]'>
                    <p className='text-base font-bold text-white mr-2 line-clamp-2'>{name}</p>
                    <div className='w-[40px] h-[40px] rounded-full bg-darkBlue/80 dark:bg-black/70 dark:border-lightGrey text-bold text-white border-lightGrey/60 border-1 flex items-center justify-center flex-shrink-0'>
                        <b className='text-lg font-inter-numbers'>{order}</b>
                    </div>
                    {progress && (
                        <div className='absolute bottom-0 left-0 h-1 w-full bg-white/25' aria-hidden='true'>
                            <div
                                className='h-full bg-green-500 transition-[width] duration-500'
                                style={{ width: `${Math.round((progress.completed / progress.total) * 100)}%` }}
                            />
                        </div>
                    )}
                </div>

                {displayOverlayContent && overlayContent && (
                    <div className='absolute top-0 left-0 z-[2] translateZ-30 w-full h-full'>
                        {overlayContent}
                    </div>
                )}
            </div>
            {showMobileWarning && (
                <div className='absolute top-4 text-center text-sm block text-[#666] sm:hidden'>
                    This effect is not optimized for mobile. Check on desktop.
                </div>
            )}
        </div>
    );
}
